import React, { useState } from 'react'
import PropTypes from 'prop-types'

import cl from 'classnames'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Add, Remove } from '@material-ui/icons'

import MarkdownText from '../MarkdownText'
import ContainedSection from '../PageSections/ContainedSection'
import useTracker from '../Tracker/useTracker'

const useAccordionStyles = makeStyles((theme) => ({
  root: {
    padding: 8,
    boxShadow: theme.customShadows[2],
  },
  rounded: {
    '&:last-child': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    '&:first-child': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
  },
}))

const useStyles = makeStyles(({ breakpoints }) => ({
  title: {
    padding: 0,
    [breakpoints.up('md')]: {
      padding: '16px 0',
    },
  },
  summaryRoot: {
    '&.Mui-expanded': {
      minHeight: '48px',
    },
    '& $content.Mui-expanded': {
      margin: '12px 0',
    },
  },
  summaryContent: {
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
  detailsRoot: {
    padding: '0 16px 16px',
  },
}))

const FaqsItem = ({ faq, index, isActive, toggleActive, questionClassName, itemClassName }) => {
  const accordionClasses = useAccordionStyles()
  const classes = useStyles()

  const handleChange = (e, expanded) => {
    toggleActive(expanded ? faq : null, index)
  }

  return (
    <Box my={3}>
      <Accordion
        expanded={isActive}
        onChange={handleChange}
        elevation={0}
        classes={{
          root: cl(accordionClasses.root, itemClassName),
          rounded: accordionClasses.rounded,
        }}
      >
        <AccordionSummary
          expandIcon={isActive ? <Remove color='primary' fontSize='large' /> : <Add color='primary' fontSize='large' />}
          classes={{
            root: accordionClasses.summaryRoot,
            content: classes.summaryContent,
          }}
        >
          <Typography variant='h3' className={cl(classes.title, questionClassName)}>
            {faq.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.detailsRoot}>
          <Typography variant='body1'>
            <MarkdownText source={faq.body} />
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

FaqsItem.propTypes = {
  faq: PropTypes.object,
  index: PropTypes.number,
  isActive: PropTypes.bool,
  toggleActive: PropTypes.func,
  questionClassName: PropTypes.string,
  itemClassName: PropTypes.string,
}

const Faqs = ({ bgcolor, faqs, trackerEvent, questionClassName, itemClassName, ...rest }) => {
  const [activeFaq, setActiveFaq] = useState(null)
  const [trackAction] = useTracker()

  const handleToggle = (faq, index) => {
    !!faq && trackerEvent && trackAction(trackerEvent, { question_id: faq.id })

    setActiveFaq(faq ? index : null)
  }

  return (
    <ContainedSection
      title={
        <Typography align='center' gutterBottom variant='h2'>
          FAQs
        </Typography>
      }
      bgcolor={bgcolor}
      {...rest}
    >
      {faqs.map((faq, index) => (
        <FaqsItem
          key={index}
          index={index}
          faq={faq}
          isActive={activeFaq === index}
          toggleActive={handleToggle}
          questionClassName={questionClassName}
          itemClassName={itemClassName}
        />
      ))}
    </ContainedSection>
  )
}

Faqs.propTypes = {
  bgcolor: PropTypes.string,
  trackerEvent: PropTypes.string,
  faqs: PropTypes.array.isRequired,
  questionClassName: PropTypes.string,
  itemClassName: PropTypes.string,
}

export default Faqs
